import { PageMeta } from "@/components/PageMeta";
import { SectionHeading } from "@/components/SectionHeading";
import { CapabilityCard } from "@/components/CapabilityCard";
import { ProductFamilyCard } from "@/components/ProductFamilyCard";
import { AvailabilityMatrix } from "@/components/AvailabilityMatrix";
import { StatusBadge } from "@/components/StatusBadge";
import { GlassPanel } from "@/components/GlassPanel";
import { CtaLink } from "@/components/Button";
import { productCapabilities } from "@/content/capabilities";
import { products } from "@/content/products";
import { availabilityMatrix } from "@/content/availability";
import { capabilityGroupLabels, type CapabilityGroup } from "@/types/content";
import { routeSeo } from "@/content/seo";

const groupOrder: CapabilityGroup[] = ["build", "execute", "govern", "extend"];

export function ProductPage() {
  const seo = routeSeo["/product"];
  return (
    <>
      <PageMeta title={seo.title} description={seo.description} path="/product" />

      <section className="container page-hero">
        <p className="eyebrow">Product</p>
        <h1 className="page-hero-title">One governed platform. Five connected product surfaces.</h1>
        <p className="page-hero-lede">
          Devory Platform, Desktop, Cloud, Runtime and Autonomous User Interface are not five
          independently completed products. They are five connected surfaces of one governed
          engineering system, each shown below with its own honest status.
        </p>
      </section>

      <section className="container-narrow section-tight prose" aria-labelledby="role-heading">
        <h2 id="role-heading" className="visually-hidden">
          What Devory is
        </h2>
        <p>
          Devory is not a workflow tool, a package manager or another AI assistant. It is the
          infrastructure layer for software composition: a governed system where trusted
          capabilities are built once, qualified and reused, by engineers and by autonomous AI
          agents, instead of being reintegrated from scratch on every project.
        </p>
      </section>

      <section className="container section" aria-labelledby="family-heading">
        <SectionHeading
          level={2}
          title={<span id="family-heading">The Devory product family</span>}
        />
        <div className="grid-3">
          {products.map((product) => (
            <ProductFamilyCard key={product.id} product={product} />
          ))}
        </div>
      </section>

      <section className="container section" aria-labelledby="availability-heading">
        <SectionHeading
          level={2}
          title={<span id="availability-heading">Current development status</span>}
          lede="Public Release 0.1 is live: self-serve sign-up, real billing and a scoped set of cloud-execution and marketplace capabilities are open to everyone today. The rest of the platform is shown below with its own honest status, not rounded up."
        />
        <AvailabilityMatrix rows={availabilityMatrix} />
      </section>

      <section className="container section" aria-label="Status legend">
        <GlassPanel className="stage-banner">
          <StatusBadge status="live" />
          <StatusBadge status="pilot" />
          <StatusBadge status="in-development" />
          <StatusBadge status="planned" />
          <p>
            &ldquo;Live&rdquo; means anyone can reach the capability today through public
            self-serve sign-up. &ldquo;Available in pilot&rdquo; means it is real and validated
            inside Devory&rsquo;s internal engineering pilot, but not yet part of that public
            surface.
          </p>
        </GlassPanel>
      </section>

      <section className="container section-tight" aria-labelledby="build-execute-heading">
        <SectionHeading
          level={2}
          title={<span id="build-execute-heading">Every capability, by engineering concern</span>}
          lede="The same platform, viewed by what each capability does rather than which product surface it belongs to."
        />
      </section>

      {groupOrder.map((group) => {
        const items = productCapabilities.filter((capability) => capability.group === group);
        if (items.length === 0) return null;
        return (
          <section
            key={group}
            className="container section-tight"
            aria-labelledby={`capabilities-${group}-heading`}
          >
            <SectionHeading
              level={2}
              eyebrow={capabilityGroupLabels[group]}
              title={
                <span id={`capabilities-${group}-heading`}>{capabilityGroupLabels[group]}</span>
              }
            />
            <div className="grid-3">
              {items.map((capability) => (
                <CapabilityCard key={capability.id} capability={capability} />
              ))}
            </div>
          </section>
        );
      })}

      <section className="container section-tight">
        <GlassPanel className="cta-banner">
          <h2>Want to see how this applies to your team?</h2>
          <p>Read how Devory maps to specific engineering roles, or get in touch directly.</p>
          <div className="cta-banner-actions">
            <CtaLink to="/solutions" variant="secondary">
              View solutions by team
            </CtaLink>
            <CtaLink to="/contact" variant="primary">
              Contact Devory
            </CtaLink>
          </div>
        </GlassPanel>
      </section>
    </>
  );
}
